import React from 'react';
import { Box, Button, Chip, Grid, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { Add, Close } from '@mui/icons-material';

import { BrandColorPicker, FileDropzone, StatesSelect } from 'components';
import { useBuildingFormManager } from './building-form.hook';
import { BuildingAreasDialog } from 'dialogs/building/building-areas-dialog';
import { IBuildingArea } from '../../../models';

interface BuildingFormProps {
  buildingId?: number | null;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const BuildingForm = React.forwardRef(
  ({ buildingId, onInit = () => {}, onSubmitting = () => {}, next }: BuildingFormProps, ref) => {
    const theme = useTheme();
    const [{ building, formManager, loading_form, loading_submit }] = useBuildingFormManager({ buildingId, next });
    const [buildingAreaDialogVisible, setBuildingAreaDialogVisible] = React.useState(false);

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loading_form), [loading_form]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return (
      <form onSubmit={formManager.handleSubmit} autoComplete="off">
        <Box paddingY={2}>
          <Grid container rowSpacing={3} columnSpacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account name"
                placeholder="e.g. Potomac Place"
                variant="outlined"
                value={formManager.values.account_name}
                onChange={formManager.handleChange('account_name')}
                error={!!(formManager.errors.account_name && formManager.touched.account_name)}
                helperText={formManager.touched.account_name ? formManager.errors.account_name : null}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Building address"
                placeholder="e.g. 350 5th Ave"
                variant="outlined"
                value={formManager.values.address}
                onChange={formManager.handleChange('address')}
                error={!!(formManager.errors.address && formManager.touched.address)}
                helperText={formManager.touched.address ? formManager.errors.address : null}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="City"
                placeholder="e.g. New York"
                variant="outlined"
                value={formManager.values.city}
                onChange={formManager.handleChange('city')}
                error={!!(formManager.errors.city && formManager.touched.city)}
                helperText={formManager.touched.city ? formManager.errors.city : null}
              />
            </Grid>

            <Grid item xs={6} sm={4}>
              <StatesSelect
                label="State"
                variant="outlined"
                fullWidth
                size="medium"
                disabled={false}
                value={formManager.values.state}
                onChange={formManager.handleChange('state')}
                error={!!(formManager.errors.state && formManager.touched.state)}
                helperText={formManager.touched.state ? formManager.errors.state?.toString() : null}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Zip"
                placeholder="e.g. 10118"
                variant="outlined"
                value={formManager.values.zip || ''}
                onChange={formManager.handleChange('zip')}
                error={!!(formManager.errors.zip && formManager.touched.zip)}
                helperText={formManager.touched.zip ? formManager.errors.zip : null}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Number of floors"
                placeholder="e.g. 4"
                variant="outlined"
                value={formManager.values.floors || ''}
                onChange={formManager.handleChange('floors')}
                error={!!(formManager.errors.floors && formManager.touched.floors)}
                helperText={formManager.touched.floors ? formManager.errors.floors : null}
                disabled={!!building?.id}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1">Building Areas</Typography>
              {formManager.touched.areas && formManager.errors.areas && (
                <Typography color="error" variant="caption">
                  {formManager.errors.areas}
                </Typography>
              )}
              {formManager.values.areas.slice(0, 5).map((a: IBuildingArea, i: number) => (
                <Chip key={i} color="primary" variant="outlined" label={a.name} sx={{ mr: 1, mb: 1 }} />
              ))}
              {formManager.values.areas.length > 5 && (
                <Typography variant="caption">+ {formManager.values.areas.slice(5).length} more areas</Typography>
              )}
              <Box>
                <Button sx={{ paddingX: 2 }} startIcon={<Add />} onClick={() => setBuildingAreaDialogVisible(true)}>
                  Manage Building Areas
                </Button>
              </Box>

              <BuildingAreasDialog
                visible={buildingAreaDialogVisible}
                setVisible={setBuildingAreaDialogVisible}
                areas={formManager.values.areas}
                setAreas={(areas) => formManager.setFieldValue('areas', areas)}
                buildingId={building?.id}
              />
            </Grid>

            <Grid item xs={12}>
              <FileDropzone
                title={'Building photo (optional)'}
                imgUrl={
                  formManager.values.image && formManager.values.image.file
                    ? URL.createObjectURL(formManager.values.image.file)
                    : formManager.values.image && formManager.values.image.url
                    ? formManager.values.image.url
                    : ''
                }
                onSelectFile={(file: File | null) => formManager.setFieldValue('image', { file })}
                error={(formManager.touched.image ? formManager.errors.image : '') as string}
              />
            </Grid>

            {!formManager.values.primary_brand_color && !formManager.values.secondary_brand_color && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Button
                    sx={{ paddingX: 2 }}
                    startIcon={<Add />}
                    onClick={() => {
                      formManager.setValues((prev) => ({
                        ...prev,
                        primary_brand_color: '#000000',
                        secondary_brand_color: '#000000',
                      }));
                    }}
                  >
                    Add Building Brand Elements
                  </Button>
                </Box>
              </Grid>
            )}

            {formManager.values.primary_brand_color && formManager.values.secondary_brand_color && (
              <>
                <Grid item xs={12}>
                  <FileDropzone
                    title={
                      "Building logo (optional). This logo will be used on zone decals instead of the organization's logo."
                    }
                    imgUrl={
                      formManager.values.logo && formManager.values.logo.file
                        ? URL.createObjectURL(formManager.values.logo.file)
                        : formManager.values.logo && formManager.values.logo.url
                        ? formManager.values.logo.url
                        : ''
                    }
                    onSelectFile={(file: File | null) => formManager.setFieldValue('logo', { file })}
                    error={(formManager.touched.logo ? formManager.errors.logo : '') as string}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
                      Building brand color (optional)
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: 'relative',
                      border: 1,
                      borderStyle: 'dashed',
                      borderRadius: 1,
                      borderColor: theme.palette.text.secondary,
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingX: 1.75,
                    }}
                  >
                    <BrandColorPicker
                      primaryColor={formManager.values.primary_brand_color}
                      onChangePrimaryColor={(value) => formManager.setFieldValue('primary_brand_color', value)}
                    />

                    <Box
                      sx={{
                        position: 'absolute',
                        top: -14,
                        right: -14,
                        bgcolor: theme.palette.primary.main,
                        borderRadius: '100%',
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();

                          formManager.setValues((prev) => ({
                            ...prev,
                            primary_brand_color: '',
                            secondary_brand_color: '',
                          }));
                        }}
                      >
                        <Close sx={{ color: theme.palette.common.white, fontSize: 'inherit' }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12} hidden>
              <Button variant="contained" fullWidth>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Box>
      </form>
    );
  },
);
